import React from 'react';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';

export type LinkVariant = 'primary' | 'secondary' | 'muted' | 'inherit';
export type LinkSize = 'small' | 'medium' | 'large';
export interface LinkProps {
  variant?: LinkVariant;
  color?: string;
  size?: LinkSize;
  href?: string;
  external?: boolean;
  underline?: 'always' | 'hover' | 'none';
  weight?: 400 | 500 | 600 | 700;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

const sizeClasses: Record<LinkSize, string> = {
  small: 'text-xs',
  medium: 'text-sm',
  large: 'text-base',
};

const weightClasses: Record<number, string> = {
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
};

const variantColors: Record<LinkVariant, { default: string; hover: string }> = {
  primary: { default: colors.infoDark, hover: colors.info },
  secondary: { default: colors.textDefault, hover: colors.infoDark },
  muted: { default: colors.textMuted, hover: colors.infoDark },
  inherit: { default: 'inherit', hover: colors.infoDark },
};

export const Link: React.FC<LinkProps> = ({
  variant = 'primary',
  color,
  size = 'medium',
  href,
  external = false,
  underline = 'hover',
  weight = 500,
  onClick,
  children,
  className = '',
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const colorConfig = variantColors[variant];
  const resolveColor = (): string => {
    if (disabled) return colors.textMuted;
    if (color) return color;
    return isHovered ? colorConfig.hover : colorConfig.default;
  };

  const resolveTextDecoration = (): string => {
    if (underline === 'always') return 'underline';
    if (underline === 'hover' && isHovered) return 'underline';
    return 'none';
  };
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <a
      href={disabled ? undefined : href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'transition-colors duration-200',
        sizeClasses[size],
        weightClasses[weight],
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className
      )}
      style={{
        color: resolveColor(),
        textDecoration: resolveTextDecoration(),
        textUnderlineOffset: '3px',
      }}
    >
      {children}
    </a>
  );
};

export const TextLink: React.FC<{
  children: React.ReactNode;
  linkText: string;
  linkProps?: Omit<LinkProps, 'children'>;
  className?: string;
}> = ({ children, linkText, linkProps, className = '' }) => {
  return (
    <span
      className={cn('text-sm', className)}
      style={{ color: colors.textDefault }}
    >
      {children} <Link {...linkProps}>{linkText}</Link>
    </span>
  );
};

export default Link;
